import React, { useState, useEffect } from 'react';
import { Search, Calendar, Clock, Tag, ArrowRight, Menu, X, Rss } from 'lucide-react';

// Configuration
const API_BASE_URL = 'https://bigtimeai-publisher.rjrejano77.workers.dev';
const SITE_CONFIG = {
  name: 'BigTime AI',
  tagline: 'Latest AI insights, analysis, and trends',
  description: 'Your source for expert AI analysis, industry insights, and cutting-edge developments in artificial intelligence.',
  url: 'https://bigtimeai.com'
};

// Main App Component
function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavigation = (page, data = null) => {
    setCurrentPage(page);
    setSelectedPost(data);
    setSelectedCategory(data);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage('search');
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      <Header 
        onNavigate={handleNavigation}
        onSearch={handleSearch}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />
      
      <main className="pt-16">
        {currentPage === 'home' && <HomePage onNavigate={handleNavigation} />}
        {currentPage === 'post' && selectedPost && <PostPage post={selectedPost} onNavigate={handleNavigation} />}
        {currentPage === 'category' && selectedCategory && <CategoryPage category={selectedCategory} onNavigate={handleNavigation} />}
        {currentPage === 'search' && <SearchPage query={searchQuery} onNavigate={handleNavigation} />}
        {currentPage === 'about' && <AboutPage />}
      </main>
      
      <Footer onNavigate={handleNavigation} />
    </div>
  );
}

// Header Component
function Header({ onNavigate, onSearch, isMobileMenuOpen, setIsMobileMenuOpen }) {
  const [searchInput, setSearchInput] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      onSearch(searchInput.trim());
      setSearchInput('');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-slate-900/30 backdrop-blur-lg border-b border-white/10 z-50 shadow-lg">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <button 
            onClick={() => onNavigate('home')}
            className="flex items-center space-x-3 group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-xl group-hover:shadow-blue-500/20 transition-all duration-300">
              <span className="text-white font-bold text-sm">AI</span>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-gray-100 to-blue-100 bg-clip-text text-transparent">
              {SITE_CONFIG.name}
            </span>
          </button>

          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => onNavigate('home')}
              className="text-gray-300 hover:text-blue-400 font-semibold transition-colors duration-300"
            >
              Home
            </button>
            <button 
              onClick={() => onNavigate('about')}
              className="text-gray-300 hover:text-blue-400 font-semibold transition-colors duration-300"
            >
              About
            </button>
            <a 
              href={`${API_BASE_URL}/api/rss`}
              className="text-gray-300 hover:text-blue-400 font-semibold transition-colors duration-300 flex items-center space-x-2"
            >
              <Rss size={16} />
              <span>RSS</span>
            </a>
          </nav>

          <div className="hidden md:flex items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(e)}
                placeholder="Discover AI insights..."
                className="pl-10 pr-4 py-2.5 w-64 border border-white/10 bg-white/5 text-white rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-300 transition-all duration-300"
              />
            </div>
            <button 
              onClick={handleSearchSubmit}
              className="ml-2 p-2 bg-blue-600 rounded-lg text-white hover:bg-blue-700 transition-colors"
            >
              <Search size={16} />
            </button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors duration-300 text-white"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 py-4 bg-slate-900/80 backdrop-blur-lg">
            <nav className="flex flex-col space-y-4">
              <button 
                onClick={() => onNavigate('home')}
                className="text-left text-gray-300 hover:text-blue-400 font-semibold py-2"
              >
                Home
              </button>
              <button 
                onClick={() => onNavigate('about')}
                className="text-left text-gray-300 hover:text-blue-400 font-semibold py-2"
              >
                About
              </button>
              <a 
                href={`${API_BASE_URL}/api/rss`}
                className="text-gray-300 hover:text-blue-400 font-semibold flex items-center space-x-2 py-2"
              >
                <Rss size={16} />
                <span>RSS Feed</span>
              </a>
              <div className="flex items-center pt-2">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(e)}
                  placeholder="Search..."
                  className="flex-1 px-4 py-2 border border-white/10 bg-white/5 text-white rounded-l-lg"
                />
                <button 
                  onClick={handleSearchSubmit}
                  className="p-2 bg-blue-600 rounded-r-lg text-white hover:bg-blue-700 transition-colors"
                >
                  <Search size={16} />
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

// HomePage Component
function HomePage({ onNavigate }) {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [stats, setStats] = useState(null);
  const postsPerPage = 9; // Request 9 posts per page

  // Fetch blog stats for analytics section
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stats`);
      if (response.ok) {
        const statsData = await response.json();
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    setLoading(true);
    
    // Fetch posts, categories, and stats
    Promise.all([
      fetch(`${API_BASE_URL}/api/posts?page=${currentPage}&limit=${postsPerPage}`).then(res => res.json()),
      fetch(`${API_BASE_URL}/api/categories`).then(res => res.json()),
      fetch(`${API_BASE_URL}/api/stats`).then(res => res.json()).catch(() => null)
    ])
    .then(([postsData, categoriesData, statsData]) => {
      console.log('Posts data:', postsData);
      console.log('Categories data:', categoriesData);
      console.log('Stats data:', statsData);
      
      setPosts(postsData.posts || []);
      
      // Update pagination state from API response
      if (postsData.pagination) {
        setTotalPages(postsData.pagination.totalPages || 1);
        setTotalPosts(postsData.pagination.totalPosts || 0);
      }
      
      setCategories(categoriesData.categories || []);
      
      // Set stats if available
      if (statsData) {
        setStats(statsData);
      }
      
      setLoading(false);
    })
    .catch(err => {
      console.error('Error loading content:', err);
      setError('Failed to load content. Make sure you have published some posts first!');
      setLoading(false);
    });
  }, [currentPage]); // Re-fetch when page changes

  // Pagination function to change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: document.getElementById('latest-insights').offsetTop - 100, behavior: 'smooth' });
  };

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section - Based on provided image */}
      <div className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-600 to-purple-700"></div>
        
        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div 
              key={i}
              className="absolute rounded-full bg-white/20 animate-float"
              style={{
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 15}s`
              }}
            ></div>
          ))}
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Pill/Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md rounded-full mb-8 animate-fade-in">
              <div className="w-2 h-2 bg-white rounded-full mr-2"></div>
              <span className="text-white text-sm font-medium">Powered by Advanced AI Analysis</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 animate-fade-in-down">
              {SITE_CONFIG.name}
            </h1>
            
            <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto animate-fade-in-up">
              {SITE_CONFIG.tagline}
            </p>
            
            <p className="text-lg text-white/90 mb-12 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              Cutting-edge artificial intelligence insights, expert analysis, and the latest breakthroughs shaping our future.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 animate-fade-in" style={{ animationDelay: '0.5s' }}>
              <button 
                onClick={() => document.getElementById('latest-insights').scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-white rounded-full text-purple-700 text-lg font-bold hover:shadow-lg hover:shadow-white/20 transition-all duration-300"
              >
                Explore Latest Insights
              </button>
              
              <button 
                onClick={() => onNavigate('about')}
                className="px-8 py-4 bg-transparent border-2 border-white/30 rounded-full text-white text-lg font-bold hover:bg-white/10 hover:shadow-lg hover:shadow-white/10 transition-all duration-300"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Posts Section */}
      <div id="latest-insights" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Latest AI Insights</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Discover the most recent developments and expert analysis in artificial intelligence
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onNavigate={onNavigate} />
          ))}
        </div>

        {/* Pagination controls - now using server-side pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-12">
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg ${currentPage === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-purple-700 text-white hover:bg-purple-600 transition-colors'}`}
              >
                Previous
              </button>
              
              <div className="flex space-x-1">
                {/* First page */}
                {currentPage > 2 && (
                  <button
                    onClick={() => paginate(1)}
                    className="w-10 h-10 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                  >
                    1
                  </button>
                )}
                
                {/* Ellipsis if needed */}
                {currentPage > 3 && (
                  <span className="w-10 h-10 flex items-center justify-center text-white">...</span>
                )}
                
                {/* Page number before current */}
                {currentPage > 1 && (
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    className="w-10 h-10 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                  >
                    {currentPage - 1}
                  </button>
                )}
                
                {/* Current page */}
                <button
                  className="w-10 h-10 rounded-lg bg-purple-600 text-white"
                >
                  {currentPage}
                </button>
                
                {/* Page number after current */}
                {currentPage < totalPages && (
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    className="w-10 h-10 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                  >
                    {currentPage + 1}
                  </button>
                )}
                
                {/* Ellipsis if needed */}
                {currentPage < totalPages - 2 && (
                  <span className="w-10 h-10 flex items-center justify-center text-white">...</span>
                )}
                
                {/* Last page */}
                {currentPage < totalPages - 1 && (
                  <button
                    onClick={() => paginate(totalPages)}
                    className="w-10 h-10 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                  >
                    {totalPages}
                  </button>
                )}
              </div>
              
              <button 
                onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg ${currentPage === totalPages ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-purple-700 text-white hover:bg-purple-600 transition-colors'}`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {totalPosts > 0 && (
          <div className="text-center text-gray-400 mt-4">
            Showing {posts.length} of {totalPosts} posts
          </div>
        )}

        {posts.length === 0 && (
          <div className="text-center py-20">
            <div className="max-w-md mx-auto bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-xl">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">🤖</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">No Posts Published Yet!</h3>
              <div className="text-sm text-gray-300 space-y-3">
                <p>🤖 Your AI system is ready to publish content</p>
                <p>📝 Use the publishing dashboard to review posts</p>
                <p>🚀 Visit dashboard.bigtimeai.com to get started!</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Analytics Section - Using stats API data */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 mb-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-white mb-4">AI Insights Dashboard</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Track our growing library of artificial intelligence content
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto animate-fade-in-up">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-blue-500/10 transition-all duration-500 hover:scale-105 hover:bg-white/15">
            <div className="text-3xl font-bold text-white mb-2">
              {stats?.totalPosts || totalPosts}
            </div>
            <div className="text-gray-300 font-medium">AI Articles Published</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-blue-500/10 transition-all duration-500 hover:scale-105 hover:bg-white/15">
            <div className="text-3xl font-bold text-white mb-2">
              {stats?.categoriesCount || categories.length}
            </div>
            <div className="text-gray-300 font-medium">Categories Covered</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-blue-500/10 transition-all duration-500 hover:scale-105 hover:bg-white/15">
            <div className="text-3xl font-bold text-white mb-2">
              {stats?.totalViews ? `${(stats.totalViews / 1000).toFixed(1)}K` : 'Daily'}
            </div>
            <div className="text-gray-300 font-medium">
              {stats?.totalViews ? 'Total Views' : 'Fresh Insights'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// PostCard Component with Glassmorphism
function PostCard({ post, onNavigate }) {
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return 'Unknown date';
    }
  };

  const getReadingTimeColor = (time) => {
    if (time <= 3) return 'text-green-400 bg-green-900/30';
    if (time <= 7) return 'text-blue-400 bg-blue-900/30';
    return 'text-purple-400 bg-purple-900/30';
  };

  return (
    <article className="group relative glassmorphism-card">
      {/* Glass effect wrapper */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-white/5 rounded-2xl backdrop-blur-lg border border-white/20 shadow-lg group-hover:shadow-blue-500/20 transition-all duration-500 group-hover:scale-[1.02]"></div>
      
      {/* Glow effect */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
      
      {/* Top gradient line */}
      <div className="relative h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 rounded-t-2xl"></div>
      
      {/* Content */}
      <div className="relative p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3 text-sm text-gray-300">
            <Calendar size={14} className="text-blue-400" />
            <span className="font-medium">{formatDate(post.publishedDate)}</span>
          </div>
          
          <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold ${getReadingTimeColor(post.readingTime)}`}>
            <Clock size={12} />
            <span>{post.readingTime} min</span>
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-blue-300 transition-colors">
          {typeof post.title === 'string' ? post.title : 'Untitled'}
        </h2>
        
        <p className="text-gray-300 mb-6 line-clamp-3">
          {typeof post.excerpt === 'string' ? post.excerpt : 'No excerpt available'}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Tag size={14} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-300 bg-gray-800/50 px-2 py-1 rounded-full">
              {typeof post.category === 'string' ? post.category : 'Uncategorized'}
            </span>
          </div>
          
          <button
            onClick={() => onNavigate('post', post)}
            className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 font-semibold text-sm transition-all duration-300 hover:bg-blue-900/30 px-3 py-2 rounded-lg"
          >
            <span>Read Article</span>
            <ArrowRight size={14} className="transform group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </div>
      </div>
    </article>
  );
}

// PostPage Component
function PostPage({ post, onNavigate }) {
  const [fullPost, setFullPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!post || !post.slug) {
      setError('Invalid post data');
      setLoading(false);
      return;
    }

    fetch(`${API_BASE_URL}/api/post?slug=${post.slug}`)
      .then(res => res.json())
      .then(data => {
        if (data && typeof data === 'object') {
          setFullPost(data);
          document.title = `${data.title || 'Article'} - ${SITE_CONFIG.name}`;
        } else {
          setError('Invalid post data received');
        }
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load post');
        setLoading(false);
      });
  }, [post.slug]);

  if (loading) {
    return <LoadingState />;
  }

  if (error || !fullPost) {
    return <ErrorState message={error || 'Post not found'} />;
  }

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return 'Unknown date';
    }
  };

  // Safely get values with fallbacks
  const postTitle = typeof fullPost.title === 'string' ? fullPost.title : 'Untitled';
  const postCategory = typeof fullPost.category === 'string' ? fullPost.category : 'Uncategorized';
  const postDate = fullPost.publishedDate || new Date().toISOString();
  const readingTime = typeof fullPost.readingTime === 'number' ? fullPost.readingTime : 5;
  const postViews = typeof fullPost.views === 'number' ? fullPost.views : 0;
  const postTags = Array.isArray(fullPost.tags) ? fullPost.tags : [];
  const postContent = typeof fullPost.content === 'string' ? fullPost.content : '';

  // Get image URL
  const imageUrl = (typeof fullPost.featuredImage === 'string' && fullPost.featuredImage) 
    ? fullPost.featuredImage 
    : generatePostImage(fullPost);

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="mb-8">
        <button
          onClick={() => onNavigate('home')}
          className="text-blue-400 hover:text-blue-300 text-sm font-medium"
        >
          ← Back to Home
        </button>
      </nav>

      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-lg p-8">
        <header className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-gray-300 mb-4">
            <Calendar size={14} />
            <span>{formatDate(postDate)}</span>
            <Clock size={14} />
            <span>{readingTime} min read</span>
            <Tag size={14} />
            <button
              onClick={() => onNavigate('category', postCategory)}
              className="text-blue-400 hover:text-blue-300"
            >
              {postCategory}
            </button>
          </div>
          
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">
            {postTitle}
          </h1>
          
          {/* Featured Image */}
          <div className="relative h-64 md:h-96 mb-8 overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900">
            {!imageError && imageUrl ? (
              <img
                src={imageUrl}
                alt={postTitle}
                className={`w-full h-full object-cover transition-opacity duration-500 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageLoaded(true)}
                onError={() => {
                  setImageError(true);
                  setImageLoaded(true);
                }}
              />
            ) : null}
            
            {/* Loading placeholder or error fallback */}
            {!imageLoaded || imageError ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-900 to-purple-900">
                {imageError ? (
                  <div className="text-center p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-2xl">AI</span>
                    </div>
                    <span className="text-gray-300">BigTime AI</span>
                  </div>
                ) : (
                  <div className="animate-pulse">
                    <div className="w-16 h-16 bg-gray-700 rounded-xl"></div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
          
          {postTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {postTags.map((tag, index) => {
                const tagText = typeof tag === 'string' ? tag : String(tag);
                return (
                  <span
                    key={`${tagText}-${index}`}
                    className="px-3 py-1 bg-blue-900/30 text-blue-300 rounded-full text-sm"
                  >
                    {tagText}
                  </span>
                );
              })}
            </div>
          )}
        </header>

        <div className="post-content max-w-none text-gray-300">
          <div dangerouslySetInnerHTML={{ __html: formatContent(postContent) }} />
        </div>

        <footer className="mt-12 pt-8 border-t border-white/10">
          <div className="text-sm text-gray-400">
            <p>Published on {formatDate(postDate)}</p>
            <p>Views: {postViews > 0 ? postViews.toLocaleString() : 'N/A'}</p>
          </div>
        </footer>
      </div>
    </article>
  );
}

// CategoryPage Component
function CategoryPage({ category, onNavigate }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/posts?category=${encodeURIComponent(category)}`)
      .then(res => res.json())
      .then(data => {
        setPosts(data.posts || []);
        setLoading(false);
        document.title = `${category} - ${SITE_CONFIG.name}`;
      })
      .catch(err => {
        setError('Failed to load category posts');
        setLoading(false);
      });
  }, [category]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="mb-8">
        <button
          onClick={() => onNavigate('home')}
          className="text-blue-400 hover:text-blue-300 text-sm font-medium"
        >
          ← Back to Home
        </button>
      </nav>

      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-white mb-4">
          {category}
        </h1>
        <p className="text-lg text-gray-300">
          {posts.length} article{posts.length !== 1 ? 's' : ''} in this category
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts.map(post => (
          <PostCard key={post.id} post={post} onNavigate={onNavigate} />
        ))}
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-300 text-lg">No posts found in this category.</p>
        </div>
      )}
    </div>
  );
}

// SearchPage Component
function SearchPage({ query, onNavigate }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!query) {
      setResults([]);
      setLoading(false);
      return;
    }

    fetch(`${API_BASE_URL}/api/search?q=${encodeURIComponent(query)}`)
      .then(res => res.json())
      .then(data => {
        setResults(data.results || []);
        setLoading(false);
        document.title = `Search: ${query} - ${SITE_CONFIG.name}`;
      })
      .catch(err => {
        setError('Failed to search posts');
        setLoading(false);
      });
  }, [query]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="mb-8">
        <button
          onClick={() => onNavigate('home')}
          className="text-blue-400 hover:text-blue-300 text-sm font-medium"
        >
          ← Back to Home
        </button>
      </nav>

      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-white mb-4">
          Search Results
        </h1>
        <p className="text-lg text-gray-300">
          {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {results.map(post => (
          <PostCard key={post.id} post={post} onNavigate={onNavigate} />
        ))}
      </div>

      {results.length === 0 && query && (
        <div className="text-center py-12">
          <p className="text-gray-300 text-lg">No posts found for "{query}". Try different keywords.</p>
        </div>
      )}
    </div>
  );
}

// AboutPage Component
function AboutPage() {
  useEffect(() => {
    document.title = `About - ${SITE_CONFIG.name}`;
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          About {SITE_CONFIG.name}
        </h1>
        <p className="text-xl text-gray-300">
          Your trusted source for AI insights and analysis
        </p>
      </div>

      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-lg p-8">
        <div className="post-content max-w-none text-gray-300">
          <p className="text-lg mb-6">
            Welcome to {SITE_CONFIG.name}, where cutting-edge artificial intelligence meets expert analysis. 
            We're dedicated to bringing you the latest developments, insights, and trends in the rapidly evolving world of AI.
          </p>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">What We Do</h2>
          <p className="mb-4">
            Our platform combines automated content discovery with expert AI analysis to deliver:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><strong className="text-blue-300">Expert Analysis:</strong> Original insights and commentary on AI developments</li>
            <li><strong className="text-blue-300">Industry Context:</strong> How AI news affects different sectors and applications</li>
            <li><strong className="text-blue-300">Actionable Insights:</strong> Practical takeaways for AI professionals and enthusiasts</li>
            <li><strong className="text-blue-300">Trend Analysis:</strong> Connecting the dots between AI developments and future implications</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">Stay Connected</h2>
          <p>
            Follow our <a href={`${API_BASE_URL}/api/rss`} className="text-blue-400 hover:text-blue-300 underline">RSS feed</a> to 
            stay updated with the latest AI insights and analysis.
          </p>
        </div>
      </div>
    </div>
  );
}

// Footer Component
function Footer({ onNavigate }) {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900/50 backdrop-blur-md border-t border-white/10 mt-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">AI</span>
              </div>
              <span className="text-xl font-bold text-white">{SITE_CONFIG.name}</span>
            </div>
            <p className="text-gray-400 text-sm">
              {SITE_CONFIG.description}
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Navigation</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <button 
                  onClick={() => onNavigate('home')}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  Home
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onNavigate('about')}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  About
                </button>
              </li>
              <li>
                <a 
                  href={`${API_BASE_URL}/api/rss`}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  RSS Feed
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Connect</h3>
            <div className="flex space-x-4">
              <a href={`${API_BASE_URL}/api/rss`} className="text-gray-400 hover:text-blue-400 transition-colors">
                <Rss size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-8 pt-8 text-center text-sm text-gray-400">
          <p>© {currentYear} {SITE_CONFIG.name}. All rights reserved.</p>
          <p className="mt-2">
            Content created with AI assistance and human editorial oversight. 
            All sources are properly attributed and credited.
          </p>
        </div>
      </div>
    </footer>
  );
}

// Utility Components
function LoadingState() {
  return (
    <div className="flex flex-col justify-center items-center min-h-96 py-20">
      <div className="w-16 h-16 border-4 border-blue-300 rounded-full animate-spin border-t-blue-600"></div>
      <p className="mt-6 text-blue-300 font-medium">Loading AI insights...</p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="text-center py-20">
      <div className="max-w-md mx-auto bg-red-900/20 backdrop-blur-md rounded-2xl p-8 border border-red-500/30">
        <h3 className="text-lg font-semibold text-red-300 mb-3">Something went wrong</h3>
        <p className="text-red-200 mb-6">{message}</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

// Utility Functions
function formatContent(content) {
  if (!content) return '';
  
  return content
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-blue-300">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/^### (.*$)/gim, '<h3 class="text-xl font-bold text-white mt-8 mb-4">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold text-white mt-8 mb-4">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold text-white mt-8 mb-4">$1</h1>');
}

// Default image generation function
function generatePostImage(post) {
  return 'https://freeimghost.net/images/2025/06/03/artificial-intelligence-new-technology-science-futuristic-abstract-human-brain-ai-technology-cpu-central-processor-unit-chipset-big-data-machine-learning-cyber-mind-domination-generative-ai-scaled-1.jpg';
}

// Add global styles for animations
const style = document.createElement('style');
style.textContent = `
  @keyframes float {
    0% { transform: translateY(0px) translateX(0px); }
    50% { transform: translateY(-20px) translateX(10px); }
    100% { transform: translateY(0px) translateX(0px); }
  }
  
  @keyframes fade-in-down {
    0% { opacity: 0; transform: translateY(-20px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes fade-in-up {
    0% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes fade-in {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
  
  .animate-float {
    animation: float linear infinite;
  }
  
  .animate-fade-in-down {
    animation: fade-in-down 0.7s ease-out forwards;
  }
  
  .animate-fade-in-up {
    animation: fade-in-up 0.7s ease-out forwards;
  }
  
  .animate-fade-in {
    animation: fade-in 0.7s ease-out forwards;
  }
  
  .glassmorphism-card {
    transform-style: preserve-3d;
    perspective: 1000px;
  }
  
  .post-content h1, .post-content h2, .post-content h3 {
    color: white;
  }
  
  .post-content a {
    color: #60a5fa;
    text-decoration: underline;
  }
  
  .post-content a:hover {
    color: #93c5fd;
  }
  
  .post-content strong {
    color: #93c5fd;
  }
`;
document.head.appendChild(style);

export default App;